import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ShoppingCart, ArrowLeft, Truck } from "lucide-react";
import { useProduct } from "../hooks/useProducts";
import { useSettings } from "../hooks/useSettings";
import toast from "react-hot-toast";
import FreeDeliveryBanner from "../components/FreeDeliveryBanner";
import {
  updateMetaTags,
  generateProductSchema,
  injectSchema,
  generateBreadcrumbSchema,
} from "../utils/seo";

const ProductDetail = () => {
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const { getDeliveryLimit, getFreeDeliveryThreshold } = useSettings();
  const deliveryLimit = getDeliveryLimit();
  const freeDeliveryThreshold = getFreeDeliveryThreshold();

  // Use React Query for product - automatic caching and refetching
  const { data: product, isLoading, error } = useProduct(id);

  useEffect(() => {
    if (product) {
      // Update meta tags for SEO
      updateMetaTags({
        title: `${product.name} - Buy Online at Best Price | Grainly`,
        description: `Buy ${product.name} online at ₹${product.price}. ${
          product.description || "Premium quality rice with fast delivery"
        }. Free delivery on orders ≥ ₹${freeDeliveryThreshold}.`,
        keywords: `${product.name}, ${product.category}, buy rice online, ${product.origin} rice, premium rice`,
        image: product.image,
        url: `https://www.grainly.in.net/product/${product._id}`,
      });

      // Inject Product Schema
      const productSchema = generateProductSchema(product);
      injectSchema(productSchema);

      // Inject Breadcrumb Schema
      const breadcrumbSchema = generateBreadcrumbSchema([
        { name: "Home", url: "https://www.grainly.in.net/" },
        { name: "Shop", url: "https://www.grainly.in.net/shop" },
        {
          name: product.name,
          url: `https://www.grainly.in.net/product/${product._id}`,
        },
      ]);
      injectSchema(breadcrumbSchema);
    }
  }, [product]);

  useEffect(() => {
    if (error) {
      toast.error("Failed to fetch product details");
      console.error("Error fetching product:", error);
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Product Not Found
          </h2>
          <Link to="/shop" className="text-[#305669] hover:text-[#1785A]">
            ← Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#305669]">
            Home
          </Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#305669]">
            Shop
          </Link>
          <span>/</span>
          <span className="text-gray-900">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="relative">
              <img
                src={selectedImage || product.image}
                alt={product.name}
                className="w-full h-96 rounded-lg shadow-lg"
              />
              {product.discount > 0 && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full font-semibold">
                  {product.discount}% OFF
                </div>
              )}
            </div>

            {/* Image Thumbnails */}
            {product.images && product.images.length > 0 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${product.name} ${index + 1}`}
                    onClick={() => setSelectedImage(img)}
                    className={`w-full h-24 rounded-lg cursor-pointer transition-all ${
                      selectedImage === img || (!selectedImage && index === 0)
                        ? "border-2 border-[#8ABEB9] ring-2 ring-[#8ABEB9]"
                        : "border-2 border-transparent hover:border-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {product.name}
              </h1>
              <p className="text-gray-600">
                {product.category} • {product.weight} • {product.origin}
              </p>
            </div>

            {/* Rating */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="h-5 w-5 fill-current text-yellow-400"
                  />
                ))}
              </div>
              <span className="text-gray-600">(4.8) • 127 reviews</span>
            </div>

            {/* Price */}
            <div className="flex items-center space-x-4">
              <span className="text-4xl font-bold text-[#305669]">
                ₹{product.price}
              </span>
              {product.originalPrice > product.price && (
                <span className="text-2xl text-gray-500 line-through">
                  ₹{product.originalPrice}
                </span>
              )}
              {product.discount > 0 && (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-semibold">
                  Save ₹{product.originalPrice - product.price}
                </span>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 rounded-full ${
                  product.stock > 10 ? "bg-green-500" : "bg-yellow-500"
                }`}
              ></div>
              <span className="text-sm font-medium">
                {product.stock > 10 ? "In Stock" : `Only ${product.stock} left`}{" "}
                • {product.stock} available
              </span>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Features</h3>
              <ul className="text-gray-600 space-y-1">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <span className="w-2 h-2 bg-[#8ABEB9] rounded-full mr-2"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Delivery Info */}
            <div className="bg-[#B7E5CD]/20 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Truck className="h-5 w-5 text-[#305669]" />
                <span className="font-semibold text-[#305669]">
                  Delivery Information
                </span>
              </div>
              <ul className="text-sm text-[#305669] space-y-1">
                <li>• Same day delivery within {deliveryLimit}km</li>
                <FreeDeliveryBanner variant="product" />
                <li>• Cash on delivery available</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <Link
                to={`/order/${product._id}`}
                className="flex-1 bg-[#305669] text-white py-3 px-6 rounded-lg hover:bg-[#1785A] transition-colors duration-200 text-center font-semibold flex items-center justify-center space-x-2"
              >
                <ShoppingCart className="h-5 w-5" />
                <span>Buy Now</span>
              </Link>
              <Link
                to="/shop"
                className="flex items-center space-x-2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Shop</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
